/** Shared KB workbench copy — B + D onboarding terminology（知识条目 / 独立要点 / 知识旅程）. */
export const KB_COPY = {
  entryListTitle: "知识条目",
  seedSection: "种子条目",
  freeSection: "自由条目",
  emptyFree: "暂无自由条目",
  emptySeeds: "暂无种子条目",
  /** 种子行徽章：完成态为「已完成」；未完成态前缀同词，完整文案为「已完成 N%」（N = 已填写比例） */
  seedCompleteBadge: "已完成",
  /** 种子页 body：结构化要点之外的可选补充 */
  bodyLabel: "补充说明与链接",
  bodyHint: "可选：背景说明、引用链接等。不是对外成稿，完整简介与 PPT 属于素材库。",
  /** 自由条目：标题 = 问题（QA 对） */
  freeTitleLabel: "问题",
  freeTitlePlaceholder: "例如：探码科技的品牌定位是什么？",
  /** 自由条目：正文 = 答案要点 */
  freeBodyLabel: "答案",
  freeBodyHint: "用要点回答上方问题；一条条目只写一个知识点。",
  freeBodyPlaceholder: "用要点回答上方问题，支持 Markdown（列表、链接等）。",
  /** 新增自由条目 Dialog：一问一答最佳实践（3 秒可读） */
  freeCreateDescription:
    "一条条目 = 一问一答。标题写成问题，正文写对应答案要点——便于检索与复用，不要粘贴整篇成稿。",
  /** 3 秒可读：知识库 = 独立知识点集合，≠ 完整文章。 */
  onboardingBody: "知识库由无数独立知识点组成——每条是一个信息点，不是完整文章。",
  onboardingBodyBrand: "品牌知识库由无数独立知识点组成——每条是一个信息点，不是完整介绍文。",
  onboardingLearnMore: "了解知识库怎么用",
  /** 折叠态：三步产品语言（收集 → 拆解 → 调取）。 */
  onboardingTeaser: "信息收集 · 知识拆解 · 知识调取",
  /** 工作台 Explainer / 面包屑 / 变更履历页：知识如何被收集、拆解、入库（路由仍为 /inbox）。 */
  journeyNav: "知识旅程",
  /** Explainer 开关打开态按钮文案（再点即关） */
  journeyCloseNav: "关闭旅程",
  journeyTitle: "知识旅程",
  /** Explainer 开关 Tooltip：关→开 */
  journeyLinkHint: "查看知识产生的过程",
  /** Explainer 开关 Tooltip：开→关（回到工作台） */
  journeyCloseHint: "关闭旅程，回到知识条目",
  inboxDescription:
    "查阅资料如何变成知识的记录；可用列表、日历或时间线切换查看最近与历史履历。",
  inboxViewList: "列表",
  inboxViewCalendar: "日历",
  inboxViewTimeline: "时间线",
  inboxEmpty: "还没有旅程记录。资料经提取转化后，履历会出现在这里。",
  inboxEmptyDay: "这一天没有旅程记录。",
  inboxStatusReceived: "已收到",
  inboxStatusUnsupported: "暂不支持提取",
  inboxStatusQueued: "提取中",
  inboxStatusIngested: "已转化成知识",
  inboxStatusFailed: "提取失败",
  newEntryMenu: "新建条目",
  searchEntriesPlaceholder: "搜索条目",
  searchEntriesEmpty: "无匹配条目",
  newFreeEntry: "新增自由条目",
  aiKnowledgeEntry: "AI 智能录入",
  createSuccess: "知识条目已创建",
  deleteConfirm: "确定删除此知识条目？",
  deleteSuccess: "知识条目已删除",
  editorEmpty: "从左侧选择知识条目后开始编辑。",
  /** 自由条目顶栏：改 slug */
  editSlugTitle: "修改地址标识",
  editSlugDescription: "用于链接与引用的短地址，仅小写字母、数字和连字符。",
  editSlugLabel: "地址标识（slug）",
  editSlugPlaceholder: "例如 brand-positioning",
  editSlugInvalid: "只能包含小写字母、数字和连字符（如 brand-positioning）",
  editSlugSuccess: "地址标识已更新",
  /** 顶栏 slug：复制正文 wikilink 引用串 [[slug]] */
  copySlugRefHint: "点击复制引用地址",
  copySlugRefSuccess: "已复制引用地址",
  breadcrumbEdit: "维护条目",
  enterpriseInboxTitle: "企业知识旅程",
  brandInboxTitle: "品牌知识旅程",
  backToWorkbench: "知识库工作台",
  progressTitle: "知识维护进度",
  progressSeed: "种子条目",
  progressSeedHint: "待维护 / 已完成",
  progressEntries: "知识条目",
  progressRichness: "丰富程度",
  progressRichnessHint: "按种子要点填写比例",
  progressInbox: "旅程待处理",
  progressUpdated: "最近更新",
  progressDone: "种子要点已齐备",
} as const;

export type KbCopyKey = keyof typeof KB_COPY;

/** 工作台流程步骤图标键（组件侧映射 lucide）。 */
export type KbOnboardingStepIcon = "source" | "points" | "use";

/** 步骤卡弱强调色（muted，避免紫光堆砌）。 */
export type KbOnboardingStepAccent = "amber" | "sky" | "emerald";

export type KbOnboardingStep = {
  icon: KbOnboardingStepIcon;
  accent: KbOnboardingStepAccent;
  /** 2–4 字短标题，供 3 秒扫读 */
  title: string;
  /** 一行极短说明 */
  description: string;
};

/**
 * 工作台「了解知识库怎么用」折叠流程步骤。
 * 产品语言：信息收集 · 知识拆解 · 知识调取（底层仍是素材 → 独立知识点 → 画像/工具）。
 */
export function kbOnboardingSteps(scope: "enterprise" | "brand"): KbOnboardingStep[] {
  const profileLabel = scope === "enterprise" ? "企业" : "品牌";
  return [
    {
      icon: "source",
      accent: "amber",
      title: "信息收集",
      description: "官网、介绍等整篇素材先收进来",
    },
    {
      icon: "points",
      accent: "sky",
      title: "知识拆解",
      description: "拆成一条条可复用信息点",
    },
    {
      icon: "use",
      accent: "emerald",
      title: "知识调取",
      description: `核对${profileLabel}事实，供工具按需引用`,
    },
  ];
}
